import type { TextareaHTMLAttributes } from 'react'
import type { FieldValues, Path, Control } from 'react-hook-form'

import { FormField, FormItem, FormLabel, FormControl, FormMessage } from 'src/shared/components/ui/forms/form'
import { AutosizeTextarea } from 'src/shared/components/ui/textareas/autosize-textarea'
import { cn } from 'src/shared/utils/cn'

interface TextareaFormFieldProps<T extends FieldValues>
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'type'> {
  name: Path<T>
  label?: string
  description?: string
  floatingLabel?: boolean
  className?: string
  fieldClassName?: string
  required?: boolean
  control?: Control<T>
  minRows?: number
  maxRows?: number
  maxLength?: number
}

export function TextareaFormField<T extends FieldValues>({
  name,
  label,
  description,
  floatingLabel = false,
  className,
  fieldClassName,
  disabled,
  required,
  control,
  minRows = 1,
  maxRows = 5,
  maxLength,
}: TextareaFormFieldProps<T>) {
  if (required) {
    label = `${label} *`
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('w-full', className)}>
          {label && (
            <div className="flex flex-row items-end justify-between">
              {!floatingLabel && <FormLabel>{label}</FormLabel>}
              {maxLength && (
                <div className="mt-1 text-right text-xs text-muted">
                  {field.value?.length || 0}/{maxLength}
                </div>
              )}
            </div>
          )}
          <FormControl>
            <AutosizeTextarea
              {...field}
              label={label}
              placeholder={description}
              minRows={minRows}
              maxRows={maxRows}
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              disabled={disabled}
              className={cn(
                'focus:outline-none focus:ring-0 active:outline-none',
                'bg-accent focus:border-accent',
                fieldClassName,
              )}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
